import React, { useCallback, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
interface Page {
  src: string;
  label: string;
}
const pages: Page[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/uHL7pktKYxU3NxMmfS6rCw/volume_1_(15).webp',
  label: 'Foundation Phase — Session 10: Ball Control Challenge'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3mLCh4QYqG5uR7P9BE68U5/volume_2_(16).webp',
  label: 'Skill Development Phase — Session 11: Shooting Mechanics Upgrade'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/j3fobYj59HZGGpXkDZGoBx/volume_3_(33).webp',
  label: 'Game Intelligence Phase — Session 28: Defensive Awareness'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3TXbvopPn3pXF8eSGDRCHA/volume_4_(21).webp',
  label: 'Team Development Phase — Session 16: End Of Game Situations'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/j8r96USME4fZp6EWHJN4Bv/volume_5_(9).webp',
  label: 'The 365 Practice Vault — Practice 4: Low Attendance Game Day'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vahcw4V6xPAPsBEz4cmDW6/volume_6_(10).webp',
  label: 'Coach Success System — Building A Complete Season'
}];
const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0
  })
};
export function InsidePreview() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const paginate = useCallback((newDirection: number) => {
    setState(([prev]) => {
      const next = (prev + newDirection + pages.length) % pages.length;
      return [next, newDirection];
    });
  }, []);
  const goTo = useCallback((target: number) => {
    setState(([prev]) => [target, target > prev ? 1 : -1]);
  }, []);
  const current = pages[index];
  return <section className="py-24 bg-navy-950 relative overflow-hidden border-t border-navy-800" aria-roledescription="carousel" aria-label="Look inside the Basketball Ready System" data-id="element-100">
      <div className="absolute inset-0 bg-stadium-glow opacity-40 pointer-events-none" data-id="element-101"></div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-102">
        <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-103">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/15 text-green-400 mb-6" data-id="element-104">
            <Eye className="w-8 h-8" data-id="element-105" />
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-anton text-4xl md:text-6xl text-white uppercase tracking-wide mb-4" data-id="element-106">
            LOOK INSIDE THE SYSTEM
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-300" data-id="element-107">
            See exactly what you get — real practice plans, coaching frameworks,
            development systems, and season planning tools from inside all 6
            volumes.
          </motion.p>
        </div>

        <div className="max-w-md mx-auto" data-id="element-108">
          <div className="relative flex items-center justify-center" data-id="element-109">
            {/* Prev */}
            <button onClick={() => paginate(-1)} aria-label="Previous page" className="absolute left-0 -translate-x-1/2 md:-translate-x-1/2 z-20 w-12 h-12 rounded-full bg-navy-800 border border-navy-700 hover:border-gold-500 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 shadow-lg" data-id="element-110">
              <ChevronLeft className="w-6 h-6" data-id="element-111" />
            </button>

            {/* Stage */}
            <div className="relative w-full aspect-[2/3] overflow-hidden rounded-2xl border border-navy-700 bg-navy-900 shadow-2xl" data-id="element-112">
              <div className="absolute -inset-2 bg-gold-500/10 blur-2xl pointer-events-none" data-id="element-113"></div>
              <AnimatePresence custom={direction} mode="popLayout" initial={false} data-id="element-114">
                <motion.img key={index} src={current.src} alt={current.label} loading="lazy" decoding="async" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.35,
                ease: 'easeInOut'
              }} className="absolute inset-0 w-full h-full object-contain" data-id="element-115" />
              </AnimatePresence>
            </div>

            {/* Next */}
            <button onClick={() => paginate(1)} aria-label="Next page" className="absolute right-0 translate-x-1/2 md:translate-x-1/2 z-20 w-12 h-12 rounded-full bg-navy-800 border border-navy-700 hover:border-gold-500 text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 shadow-lg" data-id="element-116">
              <ChevronRight className="w-6 h-6" data-id="element-117" />
            </button>
          </div>

          {/* Caption */}
          <div className="mt-6 text-center min-h-[3rem]" data-id="element-118">
            <span className="text-gold-500 font-oswald text-sm uppercase tracking-widest" data-id="element-119">
              Page {index + 1} of {pages.length}
            </span>
            <p className="text-gray-300 font-medium mt-1" aria-live="polite" data-id="element-120">
              {current.label}
            </p>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-3 mt-6" data-id="element-121">
            {pages.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === index} className={`h-2.5 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${i === index ? 'w-8 bg-gold-500' : 'w-2.5 bg-navy-700 hover:bg-navy-600'}`} data-id="element-122" />)}
          </div>
        </div>
      </div>
    </section>;
}